import React from 'react';

/**
 * FRONTECH Logo SVG Component
 *
 * Ring:  stroked brushstroke circle (dashed for sketch feel) + a heavier
 *        overlay arc on one side to give the "one end dark" nuance, plus
 *        two loose-end bits for hand-drawn texture.
 * Inner: hub with starburst, isolated left-node with spark cluster,
 *        outlined large nodes + filled small nodes.
 *
 * Variants: icon | full | animated | watermark | loader
 */
export default function FrontechLogo({
  size = 48,
  variant = 'icon',
  color,
  className = '',
}) {
  const defaultColor = color || 'currentColor';

  const ringProps = {
    cx: 48, cy: 47, r: 37,
    fill: 'none',
    stroke: defaultColor,
    strokeWidth: 2.2,
    strokeLinecap: 'round',
    strokeDasharray: '70 5 80 8 70 10',
  };

  const ringOverlay = (
    <g>
      {/* Heavier "dark end" arc — sweeps from top around to right */}
      <path
        d="M 46,10 A 37,37 0 0,1 85,50"
        fill="none"
        stroke={defaultColor}
        strokeWidth="3.4"
        strokeLinecap="round"
      />
      {/* Small loose-end bits (hand-drawn texture) */}
      <line x1="18" y1="26" x2="21" y2="23"
        stroke={defaultColor} strokeWidth="1" strokeLinecap="round" opacity="0.85" />
      <line x1="80" y1="72" x2="83" y2="70"
        stroke={defaultColor} strokeWidth="1" strokeLinecap="round" opacity="0.85" />
      <line x1="25" y1="78" x2="27" y2="80"
        stroke={defaultColor} strokeWidth="0.9" strokeLinecap="round" opacity="0.7" />
    </g>
  );

  const innerConstellation = (animate = false) => (
    <g>
      {/* Hub starburst rays */}
      <path
        d="M 48,47 l 4,0 M 48,47 l -4,0 M 48,47 l 0,4 M 48,47 l 0,-4
           M 48,47 l 2.8,2.8 M 48,47 l -2.8,2.8 M 48,47 l 2.8,-2.8 M 48,47 l -2.8,-2.8
           M 48,47 l 1.5,3.6 M 48,47 l -1.5,3.6 M 48,47 l 1.5,-3.6 M 48,47 l -1.5,-3.6
           M 48,47 l 3.6,1.5 M 48,47 l -3.6,1.5 M 48,47 l 3.6,-1.5 M 48,47 l -3.6,-1.5
           M 48,47 l 4.5,-0.8 M 48,47 l -4.5,-0.8"
        stroke={defaultColor}
        strokeWidth="0.5"
        strokeLinecap="round"
      />

      {/* Outlined large nodes */}
      <circle cx="29" cy="28" r="5.5" fill="none" stroke={defaultColor} strokeWidth="1.5"
        className={animate ? 'logo-node-pop' : ''} />
      <circle cx="70" cy="38" r="4.8" fill="none" stroke={defaultColor} strokeWidth="1.5"
        className={animate ? 'logo-node-pop' : ''}
        style={animate ? { animationDelay: '0.15s' } : {}} />

      {/* Filled nodes + hub */}
      <circle cx="48" cy="47" r="3.5" fill={defaultColor}
        className={animate ? 'logo-node-pop' : ''} />
      <circle cx="66" cy="63" r="3.2" fill={defaultColor}
        className={animate ? 'logo-node-pop' : ''}
        style={animate ? { animationDelay: '0.2s' } : {}} />
      <circle cx="34" cy="67" r="2.4" fill={defaultColor}
        className={animate ? 'logo-node-pop' : ''}
        style={animate ? { animationDelay: '0.25s' } : {}} />
      <circle cx="50" cy="24" r="1.8" fill={defaultColor}
        className={animate ? 'logo-node-pop' : ''}
        style={animate ? { animationDelay: '0.3s' } : {}} />
      <circle cx="30" cy="48" r="3.0" fill={defaultColor}
        className={animate ? 'logo-node-pop' : ''}
        style={animate ? { animationDelay: '0.1s' } : {}} />

      {/* Connection lines */}
      <line x1="48" y1="47" x2="29" y2="28" stroke={defaultColor} strokeWidth="1.2" strokeLinecap="round"
        className={animate ? 'logo-line-flow' : ''} />
      <line x1="48" y1="47" x2="70" y2="38" stroke={defaultColor} strokeWidth="1.2" strokeLinecap="round"
        className={animate ? 'logo-line-flow' : ''}
        style={animate ? { animationDelay: '0.1s' } : {}} />
      <line x1="48" y1="47" x2="66" y2="63" stroke={defaultColor} strokeWidth="1.2" strokeLinecap="round"
        className={animate ? 'logo-line-flow' : ''}
        style={animate ? { animationDelay: '0.15s' } : {}} />
      <line x1="48" y1="47" x2="34" y2="67" stroke={defaultColor} strokeWidth="1.2" strokeLinecap="round"
        className={animate ? 'logo-line-flow' : ''}
        style={animate ? { animationDelay: '0.2s' } : {}} />
      <line x1="48" y1="47" x2="50" y2="24" stroke={defaultColor} strokeWidth="1.2" strokeLinecap="round"
        className={animate ? 'logo-line-flow' : ''}
        style={animate ? { animationDelay: '0.25s' } : {}} />
      <line x1="48" y1="47" x2="30" y2="48" stroke={defaultColor} strokeWidth="1.2" strokeLinecap="round"
        className={animate ? 'logo-line-flow' : ''}
        style={animate ? { animationDelay: '0.05s' } : {}} />

      {/* Spark cluster above isolated left-node */}
      <g>
        <circle cx="28.5" cy="44.5" r="0.6" fill={defaultColor} />
        <circle cx="30"   cy="44"   r="0.6" fill={defaultColor} />
        <circle cx="31.5" cy="44.5" r="0.6" fill={defaultColor} />
        <circle cx="30"   cy="45.5" r="0.6" fill={defaultColor} />
        <line x1="30" y1="43.5" x2="30" y2="42" stroke={defaultColor} strokeWidth="0.8" strokeLinecap="round" />
      </g>
    </g>
  );

  if (variant === 'watermark') {
    return (
      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        className={`frontech-watermark ${className}`}
        aria-hidden="true"
      >
        <circle {...ringProps} strokeWidth="1.5" opacity="1" />
        <circle cx="48" cy="47" r="37" fill="none" stroke={defaultColor} strokeWidth="0.8"
          className="watermark-ring-1" />
        <circle cx="48" cy="47" r="37" fill="none" stroke={defaultColor} strokeWidth="0.8"
          className="watermark-ring-2" />
        <circle cx="48" cy="47" r="37" fill="none" stroke={defaultColor} strokeWidth="0.8"
          className="watermark-ring-3" />
        {ringOverlay}
        {innerConstellation()}
      </svg>
    );
  }

  if (variant === 'loader') {
    return (
      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        className={`frontech-loader ${className}`}
        aria-label="Loading..."
        role="status"
      >
        <circle cx="48" cy="47" r="37" fill="none" stroke={defaultColor}
          strokeWidth="2.2" strokeLinecap="round"
          strokeDasharray="60 120" opacity="0.3" />
        <circle cx="48" cy="47" r="37" fill="none" stroke={defaultColor}
          strokeWidth="2.2" strokeLinecap="round"
          strokeDasharray="60 120"
          className="loader-spin" />
        <g className="loader-pulse">
          <circle cx="29" cy="28" r="5.5" fill="none" stroke={defaultColor} strokeWidth="1.5" />
          <circle cx="70" cy="38" r="4.8" fill="none" stroke={defaultColor} strokeWidth="1.5" />
          <circle cx="48" cy="47" r="3.5" fill={defaultColor} />
          <circle cx="66" cy="63" r="3.2" fill={defaultColor} />
          <circle cx="34" cy="67" r="2.4" fill={defaultColor} />
          <circle cx="30" cy="48" r="3.0" fill={defaultColor} />
        </g>
      </svg>
    );
  }

  if (variant === 'animated') {
    return (
      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        className={className}
        aria-label="FRONTECH"
      >
        <circle {...ringProps} className="logo-ring-draw" />
        {ringOverlay}
        {innerConstellation(true)}
        <circle cx="48" cy="47" r="6" fill="none" stroke={defaultColor}
          strokeWidth="1" className="logo-hub-pulse" />
      </svg>
    );
  }

  if (variant === 'full') {
    return (
      <svg
        viewBox="0 0 160 100"
        width={size * (160 / 100)}
        height={size}
        className={className}
        aria-label="FRONTECH"
      >
        <circle {...ringProps} />
        {ringOverlay}
        {innerConstellation()}
        <text
          x="95"
          y="51"
          fill={defaultColor}
          fontFamily="Outfit, sans-serif"
          fontWeight="700"
          fontSize="14"
          letterSpacing="2"
          dominantBaseline="middle"
        >
          FRONTECH
        </text>
      </svg>
    );
  }

  // Default: 'icon'
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      aria-label="FRONTECH"
    >
      <circle {...ringProps} />
      {ringOverlay}
      {innerConstellation()}
    </svg>
  );
}
